import { useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import FieldInfoPopover from "@cle/components/FieldInfoPopover";
import InfoModal from "@cle/components/InfoModal";
import RoundedCard from "@cle/components/RoundedCard";
import CourseCertificateValidationInfoContent from "./CourseCertificateValidationInfoContent";

export type ValidationItem = {
  label: string;
  value: boolean | number | string | null | undefined;
  isValid?: boolean;
  showValidationState?: boolean;
  /**
   * Danish explanation shown when the field failed validation. Resolved by
   * `resolveFieldValidationReason` — never the API's raw English `reason`.
   *
   * Deliberately not sourced from the evidence item's `aiRejectionReason`: that
   * is a snapshot taken at analysis time and is never recomputed, so it
   * contradicts the live per-field results once the secretariat edits a field.
   */
  reason?: string | null;
  /** Spans both grid columns on wide screens; use for long free text. */
  isWide?: boolean;
  /**
   * Explains the requirement behind the field. Rendered behind an "i" next to
   * the label, so it is there on every certificate — not only the rejected ones,
   * where `reason` says what went wrong.
   */
  info?: ReactNode;
};

type DocumentValidationCardProps = {
  isAnalyzing?: boolean;
  items: ValidationItem[];
  analyzingTitle?: string;
  analyzingDescription?: string;
  infoModalTitle?: string;
  infoModalDescription?: string;
  infoModalAriaLabel?: string;
  infoModalContent?: ReactNode;
};

const MAX_VISIBLE_VALUE_LENGTH = 80;

const DEFAULT_ANALYZING_TITLE = "Kursusbeviset analyseres";
const DEFAULT_ANALYZING_DESCRIPTION =
  "Dokumentet gennemgås automatisk. Det kan tage op til 2 minutter, før valideringen er færdig.";
const DEFAULT_INFO_MODAL_TITLE = "Validering af kursusbevis";
const DEFAULT_INFO_MODAL_DESCRIPTION =
  "Sådan læser du valideringen af et kursusbevis";
const DEFAULT_INFO_MODAL_ARIA_LABEL = "Læs mere om validering af kursusbevis";

const formatValidationValue = (value: ValidationItem["value"]): string => {
  if (value == null) {
    return "Ikke angivet";
  }

  return String(value);
};

const DocumentValidationCard = ({
  isAnalyzing = false,
  items,
  analyzingTitle = DEFAULT_ANALYZING_TITLE,
  analyzingDescription = DEFAULT_ANALYZING_DESCRIPTION,
  infoModalTitle = DEFAULT_INFO_MODAL_TITLE,
  infoModalDescription = DEFAULT_INFO_MODAL_DESCRIPTION,
  infoModalAriaLabel = DEFAULT_INFO_MODAL_ARIA_LABEL,
  infoModalContent = <CourseCertificateValidationInfoContent />,
}: DocumentValidationCardProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    () => new Set(),
  );

  const handleToggleExpanded = (label: string) => {
    setExpandedItems((currentItems) => {
      const nextItems = new Set(currentItems);

      if (nextItems.has(label)) {
        nextItems.delete(label);
      } else {
        nextItems.add(label);
      }

      return nextItems;
    });
  };

  return (
    <RoundedCard
      className="cle-course-certificate-document__side-card"
      header={
        <span className="d-inline-flex align-items-center gap-2">
          Validering
        </span>
      }
      action={
        <InfoModal
          title={infoModalTitle}
          description={infoModalDescription}
          ariaLabel={infoModalAriaLabel}
        >
          {infoModalContent}
        </InfoModal>
      }
    >
      {isAnalyzing ? (
        <div
          className="cle-banner cle-banner--info gap-3 px-3 py-3"
          role="status"
          aria-live="polite"
        >
          <span
            className="spinner-border spinner-border-sm flex-shrink-0 mt-1"
            aria-hidden="true"
          />
          <div className="small">
            <p className="mb-1 fw-semibold">{analyzingTitle}</p>
            <p className="mb-0">{analyzingDescription}</p>
          </div>
        </div>
      ) : (
        <div className="cle-course-certificate-document__validation-grid">
          {items.map((item) => {
            const showValidationState = item.showValidationState === true;
            const isValid = item.isValid === true;
            const value = formatValidationValue(item.value);
            const shouldTruncate = value.length > MAX_VISIBLE_VALUE_LENGTH;
            const isExpanded = expandedItems.has(item.label);
            const displayValue =
              shouldTruncate && !isExpanded
                ? `${value.slice(0, MAX_VISIBLE_VALUE_LENGTH).trim()}...`
                : value;

            return (
              <div
                key={item.label}
                className={
                  item.isWide
                    ? "cle-course-certificate-document__validation-grid-item--wide"
                    : undefined
                }
              >
                <p className="cle-course-certificate-document__field-label mb-1 d-flex align-items-center gap-1">
                  {item.label}
                  {item.info ? (
                    <FieldInfoPopover
                      ariaLabel={`Læs mere om ${item.label.toLowerCase()}`}
                    >
                      {item.info}
                    </FieldInfoPopover>
                  ) : null}
                </p>
                <p className="mb-0 d-flex gap-2 align-items-center fs-6">
                  {showValidationState ? (
                    <>
                      <span
                        className={`cle-course-certificate-document__validation-state ${
                          isValid
                            ? "cle-course-certificate-document__validation-state--success"
                            : "cle-course-certificate-document__validation-state--error"
                        }`}
                        aria-hidden="true"
                      >
                        {isValid ? <Check size={16} /> : <X size={16} />}
                      </span>
                      {/* The icon is aria-hidden and the value carries no state, so
                          spell the verdict out for screen readers. */}
                      <span className="visually-hidden">
                        {isValid ? "Godkendt" : "Afvist"}
                      </span>
                    </>
                  ) : null}
                  <span className="cle-course-certificate-document__validation-value">
                    {displayValue}
                  </span>
                </p>
                {showValidationState && !isValid && item.reason ? (
                  <p className="cle-course-certificate-document__validation-reason mb-0 mt-1">
                    {item.reason}
                  </p>
                ) : null}
                {shouldTruncate && (
                  <button
                    type="button"
                    className="btn shadow-none btn-link btn-sm p-0 mt-1"
                    aria-expanded={isExpanded}
                    onClick={() => handleToggleExpanded(item.label)}
                  >
                    {isExpanded ? "Vis mindre" : "Vis mere"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </RoundedCard>
  );
};

export default DocumentValidationCard;
